import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Shapes } from 'lucide-react';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import { useT } from '@/lib/i18n/useT';
import { LibraryPickerDialog } from './LibraryPickerDialog';
import type { LibraryItem } from './libraryItems';

interface OutlineFile {
  category: string;
  item: string;
  colorway: string | null;
  name: string;
  url: string;
}

/**
 * CH-14 — «Elegir silueta»: el selector de la biblioteca de siluetas de merch
 * sobre el slot de la imagen de producto. Agrupa por categoría, busca por
 * ítem y enseña los tres colorways. La silueta es un placeholder del tipo de
 * ítem; la foto real sigue siendo lo primero y el ítem queda marcado como
 * provisional hasta que la suban.
 */
export function OutlinePicker({ value, onPick }: { value: string | null; onPick: (url: string) => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: queryKeys.library.merchOutlines,
    queryFn: () => api.get<OutlineFile[]>('/upload/merch-outlines'),
    staleTime: 5 * 60_000,
  });
  const items: LibraryItem[] = (q.data ?? []).map((f) => ({
    name: f.colorway ? `${f.item} · ${f.colorway}` : f.item,
    url: f.url,
    group: f.category,
  }));
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 flex w-full items-center justify-between rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-lavender)] transition-colors hover:border-[var(--color-accent-ink)]"
      >
        <span className="inline-flex items-center gap-1.5">
          <Shapes className="h-3.5 w-3.5" />
          {t('stock.outline.pick')}
        </span>
        {items.length > 0 && (
          <span className="rounded-full bg-[var(--color-accent-10)] px-1.5 text-[10px] tabular-nums text-[var(--color-accent-ink)]">
            {items.length}
          </span>
        )}
      </button>
      <LibraryPickerDialog
        open={open}
        onOpenChange={setOpen}
        title={t('stock.outline.title')}
        description={t('stock.outline.description')}
        items={items}
        value={value}
        onPick={onPick}
        isLoading={q.isLoading}
        emptyLabel={t('stock.outline.empty')}
      />
    </>
  );
}
